// AddViewSupport48Component.jsx
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Label, Table } from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import DatePicker, { registerLocale } from "react-datepicker";
import { format, parseISO } from "date-fns";
import th from "date-fns/locale/th";
import { th as thBuddhist } from "date-fns/esm/locale";
import { addYears } from "date-fns/esm/fp";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";
import { GetVMdDept } from "../../share/api/MasterDataApi";

// Export libs
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

registerLocale("th-buddhist", {
  ...th,
  options: { ...th.options, weekStartsOn: 1 },
  formatLong: thBuddhist.formatLong,
});

const thaiMonths = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"
];
const range = (start, end) => new Array(end - start).fill().map((_, i) => i + start);
const years = range(1963, new Date().getFullYear() + 8);

/* ===== PDF Thai Font helpers ===== */
function base64FromArrayBuffer(buf) {
  const b = new Uint8Array(buf);
  const c = 0x8000;
  let s = "";
  for (let i = 0; i < b.length; i += c) {
    s += String.fromCharCode.apply(null, b.subarray(i, i + c));
  }
  return btoa(s);
}
let thaiFontLoaded = false;
async function ensureThaiFontLoaded() {
  if (thaiFontLoaded) return;
  const [regRes, boldRes] = await Promise.all([
    fetch("/fonts/Sarabun-Regular.ttf"),
    fetch("/fonts/Sarabun-Bold.ttf"),
  ]);
  const [regBuf, boldBuf] = await Promise.all([
    regRes.arrayBuffer(),
    boldRes.arrayBuffer(),
  ]);
  const regB64 = base64FromArrayBuffer(regBuf);
  const boldB64 = base64FromArrayBuffer(boldBuf);
  jsPDF.API.events.push([
    "addFonts",
    function () {
      this.addFileToVFS("Sarabun-Regular.ttf", regB64);
      this.addFileToVFS("Sarabun-Bold.ttf", boldB64);
      this.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
      this.addFont("Sarabun-Bold.ttf", "Sarabun", "bold");
    },
  ]);
  new jsPDF();
  thaiFontLoaded = true;
}

/* ===== helpers ===== */
function formatMonthYearBE(date) {
  if (!date) return "";
  const be = addYears(543, date);
  return format(be, "MMMM yyyy", { locale: th });
}
function fmtMoney(n) {
  const num = Number(n || 0);
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
function toThaiDateLabel(v) {
  if (!v) return "-";
  if (typeof v === "string" && /\s[ก-๙]/.test(v)) return v;
  const d = typeof v === "string" ? parseISO(v) : v;
  const be = addYears(543, d);
  const monthName = format(be, "MMMM", { locale: th });
  return `${format(be, "dd")} ${monthName} ${format(be, "yyyy")}`;
}
const groupBy = (arr, key) =>
  arr.reduce((a, it) => {
    const k = (it[key] ?? "").toString();
    (a[k] ??= []).push(it);
    return a;
  }, {});
const isUniform = (rows, field) =>
  rows.every((r) => (r[field] ?? "") === (rows[0]?.[field] ?? ""));
function dedupeRowsByAllFields(rows) {
  const keyOf = (r) =>
    [
      r.SupportNo,
      r.SupportName,
      r.DeptName, // นับ DeptName กันซ้ำด้วย
      r.Year2,
      r.ReceiptDate,
      r.Amount,
      r.Year1,
      r.AvgMan,
      r.Perc,
      r.LawTrainMan,
      r.SpkTrainMan,
      r.PaymentAmount,
      r.PaymentDate,
    ]
      .map((v) => (v ?? "").toString().trim())
      .join("|¦|");
  const seen = new Set();
  const out = [];
  for (const r of rows) {
    const k = keyOf(r);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(r);
    }
  }
  return out;
}

/* ===== Component ===== */
export function OperationReport41Component() {
  const [reportMonth, setReportMonth] = useState("");
  const [reportMonthDate, setReportMonthDate] = useState(null);

  const user = JSON.parse(localStorage.getItem("userData") || "{}");
  const roleId = String(user?.roleId ?? user?.roldId ?? "");
  const userDeptId = String(user?.deptId ?? "all");

  const [deptsData, setDeptsData] = useState([]);
  const [deptOptions, setDeptOptions] = useState([]);
  const [deptIdFilter, setDeptIdFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  // แสดงคอลัมน์หน่วยงานเฉพาะ roldId==4 และ filter == all
  const showDeptCol =
    String(user?.roldId ?? roleId) === "4" &&
    String(deptIdFilter || "all") === "all";

  useEffect(() => {
    GetVMdDept({ setDeptsData, setLoading });
  }, []);
  useEffect(() => {
    const mapped = (deptsData || []).map((d) => ({
      value: String(d?.value ?? d?.deptId ?? d?.DeptId ?? d?.id ?? ""),
      label: String(d?.label ?? d?.deptName ?? d?.DeptName ?? d?.name ?? ""),
    }));
    if (roleId === "4") {
      setDeptOptions([{ value: "all", label: "ทุกหน่วยงาน" }, ...mapped]);
      setDeptIdFilter((p) => p ?? "all");
    } else if (roleId === "3") {
      const self =
        mapped.find((o) => o.value === userDeptId) || {
          value: userDeptId,
          label: "หน่วยงานของฉัน",
        };
      setDeptOptions([self]);
      setDeptIdFilter(userDeptId);
    } else {
      setDeptOptions(mapped);
      setDeptIdFilter(
        (p) => p || (mapped.find((o) => o.value === userDeptId)?.value ?? "")
      );
    }
  }, [deptsData, roleId, userDeptId]);
  useEffect(() => {
    if (!reportMonth) {
      setReportMonthDate(null);
      return;
    }
    const [y, m] = reportMonth.split("-").map(Number);
    setReportMonthDate(new Date(y, m - 1, 1));
  }, [reportMonth]);

  const checkAndLoadMonth = async () => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("monthYear", reportMonth === "" ? "all" : reportMonth);
      fd.append("deptIdFilter", deptIdFilter || "all");
      const res = await fetch("/api/SupportOperation/listSupportOperation41", {
        method: "POST",
        headers: { token: user.token },
        body: fd,
      });
      const j = await res.json();
      const raw = Array.isArray(j?.dataenum) ? j.dataenum : [];
      // map ตาม service ใหม่ + DeptName
      const mapped = raw.map((x) => ({
        SupportNo: x.SupportNo ?? x.supportNo ?? "",
        SupportName: x.SupportName ?? x.supportName ?? "",
        DeptName: x.DeptName ?? x.deptName ?? "",
        Year2: x.Year2 ?? x.year2 ?? "", // นำส่งเงินสมทบ: ปี พ.ศ.
        ReceiptDate: x.ReceiptDate ?? x.receiptDate ?? "", // ว/ด/ป
        Amount: x.Amount ?? x.amount ?? "", // จำนวนเงิน (บาท)
        Year1: x.Year1 ?? x.year1 ?? "", // ครบตามสัดส่วน: ปี พ.ศ.
        AvgMan: x.AvgMan ?? x.avgMan ?? "", // ลูกจ้างเฉลี่ย
        Perc: x.Perc ?? x.perc ?? "", // ร้อยละ
        LawTrainMan: x.LawTrainMan ?? x.lawTrainMan ?? "", // ต้องพัฒนา
        SpkTrainMan: x.SpkTrainMan ?? x.spktrainMan ?? "", // พัฒนาจริง
        PaymentAmount: x.PaymentAmount ?? x.paymentAmount ?? "",
        PaymentDate: x.PaymentDate ?? x.paymentDate ?? "",
      }));
      setItems(dedupeRowsByAllFields(mapped));
    } catch (e) {
      Swal.fire("โหลดข้อมูลเดือนไม่สำเร็จ", "", "error");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // รวมยอดเงินช่วยเหลือ
  const totalPayment = Object.values(groupBy(items, "SupportNo")).reduce(
    (sum, rows) => {
      if (isUniform(rows, "PaymentAmount")) {
        const v = rows[0].PaymentAmount ?? 0;
        const n = typeof v === "string" ? Number(v.replace(/,/g, "")) : Number(v);
        return sum + (isNaN(n) ? 0 : n);
      }
      return (
        sum +
        rows.reduce((s, r) => {
          const v = r.PaymentAmount ?? 0;
          const n =
            typeof v === "string" ? Number(v.replace(/,/g, "")) : Number(v);
          return s + (isNaN(n) ? 0 : n);
        }, 0)
      );
    },
    0
  );

  /* ===== สร้างข้อมูลแสดง + แผน merge สำหรับ Excel/PDF (dynamic DeptName) ===== */
  const buildDisplayRowsAndMergePlan = () => {
    const groups = Object.entries(groupBy(items, "SupportNo"));

    // Header 2 แถว (dynamic)
    // Header Row 1
    const row1 = [
      "ที่",
      "เลขที่คำขอ",
      "ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน",
      ...(showDeptCol ? ["หน่วยงานที่อนุมัติคำขอ"] : []),

      "ผู้ประกอบกิจการส่งเงินสมทบเข้ากองทุนพัฒนาฝีมือแรงงาน",
      "",
      "",
      "พัฒนาฝีมือแรงงานครบตามสัดส่วนที่กฎหมายกำหนด",
      "",
      "",
      "",
      "",
      "จำนวนเงินช่วยเหลือหรืออุดหนุน (บาท)",
      "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน",
    ];

    // Header Row 2 (แก้ใหม่แบบ FIXED INDEX)
    const row2 = showDeptCol
      ? [
        "", "", "", "",
        "ปี พ.ศ.",
        "ว/ด/ป",
        "จำนวนเงิน (บาท)",
        "ปี พ.ศ.",
        "จำนวนลูกจ้างเฉลี่ย (คน)",
        "ร้อยละ",
        "จำนวนที่ต้องพัฒนาฯ",
        "จำนวนลูกจ้างที่พัฒนา",
        "",
        "",
      ]
      : [
        "", "", "",
        "ปี พ.ศ.",
        "ว/ด/ป",
        "จำนวนเงิน (บาท)",
        "ปี พ.ศ.",
        "จำนวนลูกจ้างเฉลี่ย (คน)",
        "ร้อยละ",
        "จำนวนที่ต้องพัฒนาฯ",
        "จำนวนลูกจ้างที่พัฒนา",
        "",
        "",
      ];

    const aoa = [row1, row2];
    const merges = [];

    // 3 คอลัมน์แรก (หรือ 4 ถ้ามี Dept) rowSpan=2
    const fixedCols = showDeptCol ? 4 : 3;
    for (let c = 0; c < fixedCols; c++) {
      merges.push({ s: { r: 0, c }, e: { r: 1, c } });
    }

    // กลุ่ม "เงินสมทบ" (3 คอลัมน์)
    const grp1Start = fixedCols;
    merges.push({ s: { r: 0, c: grp1Start }, e: { r: 0, c: grp1Start + 2 } });

    // กลุ่ม "ครบตามสัดส่วน" (5 คอลัมน์)
    const grp2Start = grp1Start + 3;
    merges.push({ s: { r: 0, c: grp2Start }, e: { r: 0, c: grp2Start + 4 } });

    // คอลัมน์สุดท้าย 2 ช่อง rowSpan=2
    const payAmtIdx = grp2Start + 5;
    const payDateIdx = grp2Start + 6;
    merges.push({ s: { r: 0, c: payAmtIdx }, e: { r: 1, c: payAmtIdx } });
    merges.push({ s: { r: 0, c: payDateIdx }, e: { r: 1, c: payDateIdx } });

    // map column index ของ data row
    const COL = showDeptCol
      ? {
        No: 0,
        SupportNo: 1,
        SupportName: 2,
        DeptName: 3,
        Y2: 4,
        RecDate: 5,
        Amt: 6,
        Y1: 7,
        Avg: 8,
        Perc: 9,
        Law: 10,
        Spk: 11,
        PayAmt: 12,
        PayDate: 13,
      }
      : {
        No: 0,
        SupportNo: 1,
        SupportName: 2,
        Y2: 3,
        RecDate: 4,
        Amt: 5,
        Y1: 6,
        Avg: 7,
        Perc: 8,
        Law: 9,
        Spk: 10,
        PayAmt: 11,
        PayDate: 12,
      };

    let runIndex = 1;
    let currentRow = 2; // หลัง header 2 แถว

    groups.forEach(([sn, rows]) => {
      const span = rows.length;

      const u = {
        Dept: isUniform(rows, "DeptName"),
        Y2: isUniform(rows, "Year2"),
        Rec: isUniform(rows, "ReceiptDate"),
        Amt: isUniform(rows, "Amount"),
        Y1: isUniform(rows, "Year1"),
        Avg: isUniform(rows, "AvgMan"),
        Pc: isUniform(rows, "Perc"),
        Law: isUniform(rows, "LawTrainMan"),
        Spk: isUniform(rows, "SpkTrainMan"),
        PayAmt: isUniform(rows, "PaymentAmount"),
        PayDate: isUniform(rows, "PaymentDate"),
      };

      rows.forEach((r, idx) => {
        const row = new Array(payDateIdx + 1).fill("");

        row[COL.No] = idx === 0 ? runIndex : "";

        if (idx === 0) {
          row[COL.SupportNo] = r.SupportNo || "-";
          row[COL.SupportName] = r.SupportName || "-";
          if (span > 1) {
            merges.push({
              s: { r: currentRow, c: COL.SupportNo },
              e: { r: currentRow + span - 1, c: COL.SupportNo },
            });
            merges.push({
              s: { r: currentRow, c: COL.SupportName },
              e: { r: currentRow + span - 1, c: COL.SupportName },
            });
          }
        }

        // DeptName
        if (showDeptCol) {
          if (idx === 0) {
            row[COL.DeptName] = r.DeptName || "-";
            if (span > 1) {
              merges.push({
                s: { r: currentRow, c: COL.DeptName },
                e: { r: currentRow + span - 1, c: COL.DeptName },
              });
            }
          }
        }

        // กลุ่ม “เงินสมทบ”
        row[COL.Y2] = u.Y2 ? (idx === 0 ? r.Year2 || "-" : "") : r.Year2 || "-";
        row[COL.RecDate] = u.Rec
          ? idx === 0
            ? toThaiDateLabel(r.ReceiptDate)
            : ""
          : toThaiDateLabel(r.ReceiptDate);
        const amtStr =
          typeof r.Amount === "string" ? r.Amount : fmtMoney(r.Amount);
        row[COL.Amt] = u.Amt ? (idx === 0 ? amtStr : "") : amtStr;

        // กลุ่ม “ครบตามสัดส่วน”
        row[COL.Y1] = u.Y1 ? (idx === 0 ? r.Year1 || "-" : "") : r.Year1 || "-";
        row[COL.Avg] = u.Avg
          ? idx === 0
            ? r.AvgMan || "-"
            : ""
          : r.AvgMan || "-";
        row[COL.Perc] = u.Pc
          ? idx === 0
            ? r.Perc ?? "-"
            : ""
          : r.Perc ?? "-";
        row[COL.Law] = u.Law
          ? idx === 0
            ? r.LawTrainMan || "-"
            : ""
          : r.LawTrainMan || "-";
        row[COL.Spk] = u.Spk
          ? idx === 0
            ? r.SpkTrainMan || "-"
            : ""
          : r.SpkTrainMan || "-";

        // ช่วยเหลือ / วันที่จ่าย
        const payAmtStr =
          typeof r.PaymentAmount === "string"
            ? r.PaymentAmount
            : fmtMoney(r.PaymentAmount);
        row[COL.PayAmt] = u.PayAmt ? (idx === 0 ? payAmtStr : "") : payAmtStr;
        row[COL.PayDate] = u.PayDate
          ? idx === 0
            ? toThaiDateLabel(r.PaymentDate)
            : ""
          : toThaiDateLabel(r.PaymentDate);

        // merges ของ data (เฉพาะ uniform)
        if (span > 1 && idx === 0) {
          if (u.Y2) merges.push({ s: { r: currentRow, c: COL.Y2 }, e: { r: currentRow + span - 1, c: COL.Y2 } });
          if (u.Rec) merges.push({ s: { r: currentRow, c: COL.RecDate }, e: { r: currentRow + span - 1, c: COL.RecDate } });
          if (u.Amt) merges.push({ s: { r: currentRow, c: COL.Amt }, e: { r: currentRow + span - 1, c: COL.Amt } });
          if (u.Y1) merges.push({ s: { r: currentRow, c: COL.Y1 }, e: { r: currentRow + span - 1, c: COL.Y1 } });
          if (u.Avg) merges.push({ s: { r: currentRow, c: COL.Avg }, e: { r: currentRow + span - 1, c: COL.Avg } });
          if (u.Pc) merges.push({ s: { r: currentRow, c: COL.Perc }, e: { r: currentRow + span - 1, c: COL.Perc } });
          if (u.Law) merges.push({ s: { r: currentRow, c: COL.Law }, e: { r: currentRow + span - 1, c: COL.Law } });
          if (u.Spk) merges.push({ s: { r: currentRow, c: COL.Spk }, e: { r: currentRow + span - 1, c: COL.Spk } });
          if (u.PayAmt) merges.push({ s: { r: currentRow, c: COL.PayAmt }, e: { r: currentRow + span - 1, c: COL.PayAmt } });
          if (u.PayDate) merges.push({ s: { r: currentRow, c: COL.PayDate }, e: { r: currentRow + span - 1, c: COL.PayDate } });
        }

        aoa.push(row);
        currentRow += 1;
      });

      runIndex += 1;
    });

    return { aoa, merges, payAmtIdx, payDateIdx };
  };

  /* ===== Export actions ===== */
  const handleExportExcel = () => {
    if (!items.length) {
      Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
      return;
    }

    const { aoa, merges } = buildDisplayRowsAndMergePlan();

    /* -----------------------------
     * FIX 1: ทำให้ header ทั้ง 2 แถวมีจำนวนคอลัมน์เท่ากับ data row ที่มากที่สุด
     * ----------------------------- */
    const fixHeaderColumns = () => {
      const colCount = Math.max(...aoa.map((r) => r.length));

      // บังคับให้ header แถว 0–1 มีจำนวนคอลัมน์ครบ
      for (let i = 0; i < 2; i++) {
        while (aoa[i].length < colCount) aoa[i].push("");
      }

      // บังคับ data row ให้ครบเหมือน header
      aoa.forEach((r) => {
        while (r.length < colCount) r.push("");
      });

      return colCount;
    };

    const colCount = fixHeaderColumns();

    /* -----------------------------
     * FIX 2: validate merges — ห้ามเกินจำนวนคอลัมน์
     * ----------------------------- */
    const safeMerges = merges.filter((m) => {
      return (
        m.s.c < colCount &&
        m.e.c < colCount &&
        m.s.r < aoa.length &&
        m.e.r < aoa.length
      );
    });

    /* ----------------------------- */
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!merges"] = safeMerges;

    /* -----------------------------
     * FIX 3: คำนวณขนาดคอลัมน์แบบอัตโนมัติ
     * ----------------------------- */
    ws["!cols"] = Array.from({ length: colCount }).map((_, colIndex) => {
      const maxLen = Math.max(
        ...aoa.map((r) => (r[colIndex] ? r[colIndex].toString().length : 0))
      );
      return { wch: Math.min(Math.max(maxLen + 4, 10), 50) };
    });

    /* ----------------------------- */
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      "รายงานผลการพิจารณาอนุมัติ ชอ4-1"
    );

    XLSX.writeFile(
      wb,
      "แบบรายงานผลการพิจารณาอนุมัติเงินช่วยเหลือ_ชอ4-1.xlsx"
    );
  };



  const handleExportPDF = async () => {
    if (!items.length) {
      Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
      return;
    }
    await ensureThaiFontLoaded();

    const { aoa } = buildDisplayRowsAndMergePlan();

    // ทำ head 2 แถวแบบ dynamic (colSpan/rowSpan)
    const head = [
      [
        { content: "ที่", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
        { content: "เลขที่คำขอ", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
        { content: "ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
        ...(showDeptCol
          ? [{ content: "หน่วยงานที่อนุมัติคำขอ", rowSpan: 2, styles: { halign: "center", valign: "middle" } }]
          : []),
        { content: "ผู้ประกอบกิจการส่งเงินสมทบเข้ากองทุนพัฒนาฝีมือแรงงาน", colSpan: 3, styles: { halign: "center", valign: "middle" } },
        { content: "พัฒนาฝีมือแรงงานครบตามสัดส่วนที่กฎหมายกำหนด", colSpan: 5, styles: { halign: "center", valign: "middle" } },
        { content: "จำนวนเงินช่วยเหลือหรืออุดหนุน (บาท)", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
        { content: "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
      ],
      [
        { content: "ปี พ.ศ." }, { content: "ว/ด/ป" }, { content: "จำนวนเงิน (บาท)" },
        { content: "ปี พ.ศ." }, { content: "จำนวนลูกจ้างเฉลี่ย (คน)" }, { content: "ร้อยละ" },
        { content: "จำนวนที่ต้องพัฒนาตามกฎหมาย (คน)" }, { content: "จำนวนลูกจ้างที่ได้รับการพัฒนาฯ (คน)" },
      ],
    ];

    // body (ตัด 2 header แถวแรกออก)
    const [, , ...body] = aoa;

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
    doc.setFont("Sarabun", "normal");
    doc.setFontSize(14);
    const title = `แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ4-1`;
    doc.text(title, 40, 40);

    // index สำหรับจัดแนวคอลัมน์
    const COL_IDX = showDeptCol
      ? {
        No: 0,
        SupportNo: 1,
        SupportName: 2,
        DeptName: 3,
        Y2: 4,
        RecDate: 5,
        Amt: 6,
        Y1: 7,
        Avg: 8,
        Perc: 9,
        Law: 10,
        Spk: 11,
        PayAmt: 12,
        PayDate: 13,
      }
      : {
        No: 0,
        SupportNo: 1,
        SupportName: 2,
        Y2: 3,
        RecDate: 4,
        Amt: 5,
        Y1: 6,
        Avg: 7,
        Perc: 8,
        Law: 9,
        Spk: 10,
        PayAmt: 11,
        PayDate: 12,
      };

    doc.autoTable({
      head,
      body,
      startY: 60,
      styles: {
        font: "Sarabun",
        fontSize: 10,
        cellPadding: 4,
        halign: "center",
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [255, 236, 224],
        textColor: [0, 0, 0],
        font: "Sarabun",
        fontStyle: "bold",
      },
      columnStyles: {
        [COL_IDX.SupportName]: { halign: "left" },
        ...(showDeptCol ? { [COL_IDX.DeptName]: { halign: "left" } } : {}),
        [COL_IDX.Amt]: { halign: "right" },
        [COL_IDX.Avg]: { halign: "right" },
        [COL_IDX.Perc]: { halign: "right" },
        [COL_IDX.Law]: { halign: "right" },
        [COL_IDX.Spk]: { halign: "right" },
        [COL_IDX.PayAmt]: { halign: "right" },
      },
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFont("Sarabun", "normal");
        doc.setFontSize(9);
        doc.text(
          `หน้า ${data.pageNumber} / ${pageCount}`,
          data.settings.margin.left,
          doc.internal.pageSize.getHeight() - 10
        );
      },
    });

    const filename = `แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ4-1.pdf`;
    doc.save(filename);
  };

  /* ===== render ===== */
  return (
    <Container className="py-3">
      <Row className="g-3 align-items-end">
        <Col md={6}>
          <Label>หน่วยงานกรมพัฒนาฝีมือแรงงาน</Label>
          <Select
            options={deptOptions}
            value={deptOptions.find((o) => o.value === (deptIdFilter || "all")) || null}
            onChange={(opt) => setDeptIdFilter(opt?.value ?? "all")}
            isDisabled={roleId === "3"}
            className="text-center input-color"
            styles={{
              control: (b) => ({ ...b, borderRadius: "20px" }),
              singleValue: (b) => ({ ...b, color: "#0A66C2" }),
            }}
            placeholder="เลือกหน่วยงาน"
            noOptionsMessage={() => "ไม่พบหน่วยงาน"}
          />
        </Col>
        <Col md={6}>
          <Label>รอบรายงาน (เดือน/ปี)</Label>
          <DatePicker
            selected={reportMonthDate}
            onChange={(date) => {
              if (date instanceof Date && !isNaN(date)) {
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, "0");
                setReportMonth(`${y}-${m}`);
                setReportMonthDate(new Date(y, Number(m) - 1, 1));
              } else {
                setReportMonth("");
                setReportMonthDate(null);
              }
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker
            showFullMonthYearPicker
            locale="th-buddhist"
            placeholderText="เลือกเดือน/ปี (พ.ศ.)"
            value={formatMonthYearBE(reportMonthDate)}
            renderCustomHeader={({
              date,
              changeYear,
              changeMonth,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled,
            }) => (
              <div
                className="d-flex justify-content-between align-items-center px-2 py-1"
                style={{ gap: 8 }}
              >
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={decreaseMonth}
                  disabled={prevMonthButtonDisabled}
                >
                  ‹
                </button>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 120 }}
                    value={thaiMonths[getMonth(date)]}
                    onChange={({ target: { value } }) =>
                      changeMonth(thaiMonths.indexOf(value))
                    }
                  >
                    {thaiMonths.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 100 }}
                    value={getYear(date)}
                    onChange={({ target: { value } }) =>
                      changeYear(Number(value))
                    }
                  >
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y + 543}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={increaseMonth}
                  disabled={nextMonthButtonDisabled}
                >
                  ›
                </button>
              </div>
            )}
            customInput={
              <input
                className="form-control rounded-pill text-center input-color"
                readOnly
                value={formatMonthYearBE(reportMonthDate)}
                placeholder="เลือกเดือน/ปี (พ.ศ.)"
              />
            }
          />
        </Col>

        <Col md="auto" className="pt-4 d-flex justify-content-end gap-2">
          <button className="btn btn-primary" onClick={checkAndLoadMonth}>
            ตรวจสอบ/โหลดรายงาน
          </button>
          <button className="btn btn-outline-success" onClick={handleExportExcel}>
            ส่งออก Excel
          </button>
          <button className="btn btn-outline-danger" onClick={handleExportPDF}>
            ส่งออก PDF
          </button>
        </Col>
      </Row>

      <div className="mt-3">
        <Table bordered responsive>
          <thead className="table-light">
            <tr>
              <th rowSpan={2} className="text-center align-middle">
                ที่
              </th>
              <th rowSpan={2} className="text-center align-middle">
                เลขที่คำขอ
              </th>
              <th rowSpan={2} className="text-center align-middle">
                ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน
              </th>
              {showDeptCol && (
                <th rowSpan={2} className="text-center align-middle">
                  หน่วยงานที่อนุมัติคำขอ
                </th>
              )}
              <th colSpan={3} className="text-center align-middle">
                ผู้ประกอบกิจการส่งเงินสมทบเข้ากองทุนพัฒนาฝีมือแรงงาน
              </th>
              <th colSpan={5} className="text-center align-middle">
                พัฒนาฝีมือแรงงานครบตามสัดส่วนที่กฎหมายกำหนด
              </th>
              <th rowSpan={2} className="text-center align-middle">
                จำนวนเงินช่วยเหลือหรืออุดหนุน (บาท)
              </th>
              <th rowSpan={2} className="text-center align-middle">
                วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน
              </th>
            </tr>
            <tr>
              <th className="text-center">ปี พ.ศ.</th>
              <th className="text-center">ว/ด/ป</th>
              <th className="text-center">จำนวนเงิน (บาท)</th>
              <th className="text-center">ปี พ.ศ.</th>
              <th className="text-center">จำนวนลูกจ้างเฉลี่ย (คน)</th>
              <th className="text-center">ร้อยละ</th>
              <th className="text-center">
                จำนวนที่ต้องพัฒนาตามกฎหมาย (คน)
              </th>
              <th className="text-center">
                จำนวนลูกจ้างที่ได้รับการพัฒนาฯ (คน)
              </th>
            </tr>
          </thead>

          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={showDeptCol ? 14 : 13} className="text-center">
                  ยังไม่มีรายการ
                </td>
              </tr>
            )}

            {Object.entries(groupBy(items, "SupportNo")).map(
              ([sn, rows], gIdx) => {
                const span = rows.length;
                const uPayAmt = isUniform(rows, "PaymentAmount");
                const uPayDate = isUniform(rows, "PaymentDate");
                const uY2 = isUniform(rows, "Year2");
                const uRec = isUniform(rows, "ReceiptDate");
                const uAmt = isUniform(rows, "Amount");
                const uY1 = isUniform(rows, "Year1");
                const uAvg = isUniform(rows, "AvgMan");
                const uPc = isUniform(rows, "Perc");
                const uLaw = isUniform(rows, "LawTrainMan");
                const uSpk = isUniform(rows, "SpkTrainMan");

                return rows.map((r, idx) => (
                  <tr key={`${sn}-${idx}`}>
                    <td className="text-center">{idx === 0 ? gIdx + 1 : ""}</td>
                    {idx === 0 && (
                      <td className="text-center" rowSpan={span}>
                        {r.SupportNo || "-"}
                      </td>
                    )}
                    {idx === 0 && (
                      <td className="text-start" rowSpan={span}>
                        {r.SupportName || "-"}
                      </td>
                    )}
                    {showDeptCol && idx === 0 && (
                      <td className="text-start" rowSpan={span}>
                        {r.DeptName || "-"}
                      </td>
                    )}

                    {/* นำส่งเงินสมทบ */}
                    {uY2 ? (
                      idx === 0 ? (
                        <td className="text-center" rowSpan={span}>
                          {r.Year2 || "-"}
                        </td>
                      ) : null
                    ) : (
                      <td className="text-center">{r.Year2 || "-"}</td>
                    )}
                    {uRec ? (
                      idx === 0 ? (
                        <td className="text-center" rowSpan={span}>
                          {toThaiDateLabel(r.ReceiptDate)}
                        </td>
                      ) : null
                    ) : (
                      <td className="text-center">
                        {toThaiDateLabel(r.ReceiptDate)}
                      </td>
                    )}
                    {uAmt ? (
                      idx === 0 ? (
                        <td className="text-end" rowSpan={span}>
                          {typeof r.Amount === "string"
                            ? r.Amount
                            : fmtMoney(r.Amount)}
                        </td>
                      ) : null
                    ) : (
                      <td className="text-end">
                        {typeof r.Amount === "string"
                          ? r.Amount
                          : fmtMoney(r.Amount)}
                      </td>
                    )}

                    {/* พัฒนาฝีมือครบตามฯ */}
                    {uY1 ? (
                      idx === 0 ? (
                        <td className="text-center" rowSpan={span}>
                          {r.Year1 || "-"}
                        </td>
                      ) : null
                    ) : (
                      <td className="text-center">{r.Year1 || "-"}</td>
                    )}
                    {uAvg ? (
                      idx === 0 ? (
                        <td className="text-end" rowSpan={span}>
                          {r.AvgMan || "-"}
                        </td>
                      ) : null
                    ) : (
                      <td className="text-end">{r.AvgMan || "-"}</td>
                    )}
                    {uPc ? (
                      idx === 0 ? (
                        <td className="text-end" rowSpan={span}>
                          {r.Perc ?? "-"}
                        </td>
                      ) : null
                    ) : (
                      <td className="text-end">{r.Perc ?? "-"}</td>
                    )}
                    {uLaw ? (
                      idx === 0 ? (
                        <td className="text-end" rowSpan={span}>
                          {r.LawTrainMan || "-"}
                        </td>
                      ) : null
                    ) : (
                      <td className="text-end">{r.LawTrainMan || "-"}</td>
                    )}
                    {uSpk ? (
                      idx === 0 ? (
                        <td className="text-end" rowSpan={span}>
                          {r.SpkTrainMan || "-"}
                        </td>
                      ) : null
                    ) : (
                      <td className="text-end">{r.SpkTrainMan || "-"}</td>
                    )}

                    {/* เงินช่วยเหลือ/วันที่จ่าย */}
                    {uPayAmt ? (
                      idx === 0 ? (
                        <td className="text-end" rowSpan={span}>
                          {typeof r.PaymentAmount === "string"
                            ? r.PaymentAmount
                            : fmtMoney(r.PaymentAmount)}
                        </td>
                      ) : null
                    ) : (
                      <td className="text-end">
                        {typeof r.PaymentAmount === "string"
                          ? r.PaymentAmount
                          : fmtMoney(r.PaymentAmount)}
                      </td>
                    )}
                    {uPayDate ? (
                      idx === 0 ? (
                        <td className="text-center" rowSpan={span}>
                          {toThaiDateLabel(r.PaymentDate)}
                        </td>
                      ) : null
                    ) : (
                      <td className="text-center">
                        {toThaiDateLabel(r.PaymentDate)}
                      </td>
                    )}
                  </tr>
                ));
              }
            )}
          </tbody>

          {items.length > 0 && (
            <tfoot>
              <tr>
                <td
                  colSpan={showDeptCol ? 12 : 11}
                  className="text-end fw-bold"
                >
                  รวมจำนวนเงินช่วยเหลือ
                </td>
                <td className="text-end fw-bold">{fmtMoney(totalPayment)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {loading && (
        <div className="position-fixed bottom-0 end-0 p-3">
          <div className="badge bg-secondary">กำลังโหลด...</div>
        </div>
      )}
    </Container>
  );
}
